import { useEffect, type RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      // Only elements that are actually rendered and visible.
      (el.offsetParent !== null || el === document.activeElement)
  )
}

/**
 * Traps Tab / Shift+Tab cycling inside the element referenced by `ref`
 * while `active` is true. Moves initial focus to the first focusable
 * element on activation and restores focus to the previously-focused
 * element on deactivation.
 *
 * Exposed as a standalone hook so Modal / ConfirmDialog can share it
 * and callers with custom shells can opt in too.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement>,
  active: boolean
): void {
  useEffect(() => {
    if (!active) return
    if (typeof document === 'undefined') return

    const container = ref.current
    if (!container) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    // Move focus into the container on activation.
    const focusables = getFocusable(container)
    const initial = focusables[0] ?? container
    initial.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const current = getFocusable(container)
      if (current.length === 0) {
        event.preventDefault()
        container.focus()
        return
      }

      const first = current[0]
      const last = current[current.length - 1]
      const activeEl = document.activeElement as HTMLElement | null

      if (event.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          event.preventDefault()
          last.focus()
        }
      } else if (activeEl === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus()
      }
    }
  }, [ref, active])
}

export default useFocusTrap
